import { useRef, useState, type ReactNode } from "react";

type Props = {
  cards: Array<{ key: string; label: string; node: ReactNode }>;
};

/**
 * Mobile-only swipeable card carousel. One card per screen, horizontal snap
 * scrolling, with a dot indicator and the card label. Used below the md
 * breakpoint; the desktop grid is rendered separately and untouched.
 */
export function MobileCarousel({ cards }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  const goTo = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Dot + label indicator */}
      <div className="flex items-center justify-between px-3 py-2 flex-shrink-0">
        <span className="text-[12px] font-semibold text-d_text2 tracking-[0.02em]">{cards[index]?.label}</span>
        <div className="flex gap-1.5">
          {cards.map((c, i) => (
            <button
              key={c.key}
              onClick={() => goTo(i)}
              aria-label={`Go to ${c.label}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-5 bg-d_accent" : "w-1.5 bg-d_text/20"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Scroll container */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 flex overflow-x-auto snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "none" }}
      >
        {cards.map((c) => (
          <div key={c.key} className="w-full flex-shrink-0 snap-center px-2 pb-2 h-full min-h-0">
            <div className="h-full min-h-0">{c.node}</div>
          </div>
        ))}
      </div>

      {/* Prev / next */}
      <div className="flex items-center justify-between px-3 py-1.5 flex-shrink-0">
        <button
          onClick={() => goTo(Math.max(0, index - 1))}
          disabled={index === 0}
          className="text-[11px] font-semibold uppercase tracking-[0.1em] text-d_text2 disabled:opacity-30 px-2 py-1"
        >
          ‹ Prev
        </button>
        <span className="text-[10px] text-d_text3">{index + 1} / {cards.length}</span>
        <button
          onClick={() => goTo(Math.min(cards.length - 1, index + 1))}
          disabled={index === cards.length - 1}
          className="text-[11px] font-semibold uppercase tracking-[0.1em] text-d_text2 disabled:opacity-30 px-2 py-1"
        >
          Next ›
        </button>
      </div>
    </div>
  );
}
